import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  ArrowRight, MessageCircle, ShieldCheck, Clock, Globe, BedDouble,
  Stethoscope, Sparkles, CheckCircle2, Loader2, Send, MapPin, Plane, AlertTriangle,
  Building2, Waves, Activity, Droplets, Thermometer, Sun, HeartPulse, Scissors, Pill,
  HelpCircle, ChevronDown,
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { submitContactForm } from '../lib/api';
import { useContactInfo, getWhatsAppHref } from '../contexts/ContactInfoContext';
import { track } from '../lib/analytics';
import { UrgentConditionsServices } from '../components/UrgentCareSections';

/**
 * /urgent-medical-care-hoi-an
 *
 * Dedicated landing page for urgent medical care for tourists & expats in
 * Hoi An. Stays entirely inside VietClinics branding — no direct clinic
 * numbers or external links. All conversion paths route through the
 * VietClinics request form or the platform's WhatsApp deep-link.
 */

const HERO_IMAGE = 'https://images.unsplash.com/photo-1576091160550-2173dba999ef?auto=format&fit=crop&w=1200&q=80';
const HERO_FALLBACK = 'https://images.pexels.com/photos/7088530/pexels-photo-7088530.jpeg?auto=compress&cs=tinysrgb&w=1200';

const TRUST_BADGES = [
  { icon: ShieldCheck, label: 'Doctor Arrives Within 60 Minutes' },
  { icon: Clock,       label: '24/7 Doctor Call-Outs' },
  { icon: Globe,       label: 'English-Speaking Support' },
  { icon: Plane,       label: 'Tourist & Expat Healthcare' },
  { icon: BedDouble,   label: 'Hotel & Clinic Care' },
  { icon: Sparkles,    label: 'High-Quality Patient Experience' },
];

const SERVICES = [
  {
    id: 'hotel-doctor-visit',
    name: 'Hotel Doctor Visit',
    price: 'USD 149',
    image: 'https://images.unsplash.com/photo-1666214280391-8ff5bd3c0bf0?auto=format&fit=crop&w=900&q=80',
    fallback: 'https://images.pexels.com/photos/4173251/pexels-photo-4173251.jpeg?auto=compress&cs=tinysrgb&w=900',
    desc: 'General medical consultation at your hotel — examination, diagnosis and on-the-spot medical advice from a licensed local doctor.',
    eyebrow: 'In-Hotel Care',
  },
  {
    id: 'iv-fluid-therapy',
    name: 'IV Fluid Therapy at Hotel',
    price: 'USD 119',
    image: 'https://images.unsplash.com/photo-1631815588090-d4bfec5b1ccb?auto=format&fit=crop&w=900&q=80',
    fallback: 'https://images.pexels.com/photos/5722166/pexels-photo-5722166.jpeg?auto=compress&cs=tinysrgb&w=900',
    desc: 'Dehydration, food poisoning, viral illness, hangover recovery and travel fatigue — IV rehydration delivered to your room.',
    eyebrow: 'In-Hotel Care',
  },
  {
    id: 'wound-care',
    name: 'Wound Dressing / Wound Care at Hotel',
    price: 'USD 39',
    image: 'https://images.unsplash.com/photo-1666214280557-f1b5022eb634?auto=format&fit=crop&w=900&q=80',
    fallback: 'https://images.pexels.com/photos/8460158/pexels-photo-8460158.jpeg?auto=compress&cs=tinysrgb&w=900',
    desc: 'Sterile cleaning, dressing change and wound monitoring — keep injuries from your trip clean and properly cared for.',
    eyebrow: 'In-Hotel Care',
  },
  {
    id: 'online-consultation',
    name: 'Online Medical Consultation',
    price: 'USD 29',
    image: 'https://images.unsplash.com/photo-1622253692010-333f2da6031d?auto=format&fit=crop&w=900&q=80',
    fallback: 'https://images.pexels.com/photos/4226140/pexels-photo-4226140.jpeg?auto=compress&cs=tinysrgb&w=900',
    desc: 'Speak with an English-speaking doctor by video — perfect for non-urgent symptoms, prescription guidance and travel triage.',
    eyebrow: 'Telehealth',
  },
  {
    id: 'clinic-consultation',
    name: 'Consultation at Local Clinic',
    price: 'From USD 39',
    priceNote: 'USD 39 · 07:00 AM – 05:00 PM  ·  USD 49 · 05:00 PM – 09:00 PM',
    image: 'https://images.unsplash.com/photo-1666214280165-3b0c5b9eaa3a?auto=format&fit=crop&w=900&q=80',
    fallback: 'https://images.pexels.com/photos/247786/pexels-photo-247786.jpeg?auto=compress&cs=tinysrgb&w=900',
    desc: 'Visit a verified VietClinics partner clinic in Da Nang or Hoi An — convenient, modern facility with English-speaking staff and full diagnostics.',
    eyebrow: 'In-Clinic Care',
  },
];

const STEPS = [
  { n: 1, title: 'Request a Doctor Call-Out', desc: 'Submit the short form below or message us on WhatsApp.' },
  { n: 2, title: 'A Doctor Is Dispatched',    desc: 'We dispatch a licensed English-speaking doctor to your address, arriving within 60 minutes.' },
  { n: 3, title: 'VietClinics Confirms',  desc: 'You receive confirmation with the doctor’s ETA and the all-in price.' },
  { n: 4, title: 'You Receive Care',      desc: 'A doctor visits your hotel, or you attend the clinic — VietClinics stays with you.' },
];

const DA_NANG_AREAS = ['My Khe Beach', 'Son Tra', 'Hai Chau'];
const HOI_AN_AREAS = ['Ancient Town', 'An Bang Beach'];

const SYMPTOMS = [
  { icon: Pill,        label: 'Food poisoning' },
  { icon: Activity,    label: 'Vomiting or diarrhoea' },
  { icon: Droplets,    label: 'Dehydration' },
  { icon: Thermometer, label: 'Fever or flu-like symptoms' },
  { icon: Sun,         label: 'Heat-related illness' },
  { icon: HeartPulse,  label: 'Allergic reactions' },
  { icon: Scissors,    label: 'Minor injuries & wounds' },
];

const SERVICE_OPTIONS = SERVICES.map((s) => s.name);

const COUNTRIES = [
  'Australia', 'Canada', 'France', 'Germany', 'India', 'Indonesia',
  'Japan', 'Malaysia', 'Netherlands', 'New Zealand', 'Philippines',
  'Singapore', 'South Korea', 'Thailand', 'United Kingdom',
  'United States', 'Vietnam', 'Other',
];

const inputCls =
  'w-full h-11 px-3.5 rounded-xl border border-slate-200 bg-white text-sm text-slate-900 '
  + 'placeholder:text-slate-400 focus:outline-none focus:border-[#1FA7A1] focus:ring-2 '
  + 'focus:ring-[#1FA7A1]/15 transition-colors';

const Field = ({ label, children, className = '' }) => (
  <label className={`block ${className}`}>
    <span className="block text-xs font-semibold text-slate-600 mb-1.5">{label}</span>
    {children}
  </label>
);

/* ───────────────────────────────────────────────────────────────────────── */

const UrgentMedicalCareHoiAnPage = () => {
  const { contactInfo } = useContactInfo();
  const whatsappHref = getWhatsAppHref(
    contactInfo,
    'Hi VietClinics! I need urgent medical care in Da Nang or Hoi An. Can you help?'
  );

  const [form, setForm] = useState({
    fullName: '', whatsapp: '', email: '', country: '',
    service: '', date: '', symptoms: '',
  });
  const [status, setStatus] = useState({ loading: false, sent: false, error: '' });

  const onChange = (k) => (e) =>
    setForm((prev) => ({ ...prev, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.fullName || !form.whatsapp || !form.service) {
      setStatus({ loading: false, sent: false, error: 'Please complete your name, WhatsApp number and preferred service.' });
      return;
    }
    setStatus({ loading: true, sent: false, error: '' });

    const message = [
      `URGENT — Da Nang / Hoi An Medical Care Request from ${form.fullName}.`,
      `Service: ${form.service}`,
      `WhatsApp: ${form.whatsapp}`,
      form.email && `Email: ${form.email}`,
      form.country && `Country: ${form.country}`,
      form.date && `Preferred date/time: ${form.date}`,
      form.symptoms && `Symptoms / message: ${form.symptoms}`,
    ].filter(Boolean).join('\n');

    try {
      await submitContactForm({
        name: form.fullName,
        email: form.email || `wa-${form.whatsapp.replace(/[^0-9]/g, '')}@vietclinics-request.com`,
        phone: form.whatsapp,
        subject: `Urgent Da Nang / Hoi An Care — ${form.service}`,
        message,
      });
      track('urgent_care_hoi_an_submit', { service: form.service, status: 'delivered' });
    } catch (err) {
      // Form never blocks the conversion path — log + fall back to success.
      // eslint-disable-next-line no-console
      console.warn('[UrgentHoiAn] submission failed, falling back', err);
      track('urgent_care_hoi_an_submit', { service: form.service, status: 'fallback' });
    }
    setStatus({ loading: false, sent: true, error: '' });
  };

  /* ── SEO JSON-LD (MedicalBusiness + FAQPage + BreadcrumbList) ─────────── */
  const HOI_AN_FAQ = [
    { q: 'Is the doctor call-out service available 24/7?', a: 'Yes. VietClinics coordinates urgent, non-emergency doctor call-outs in Da Nang and Hoi An 24 hours a day, 7 days a week — including evenings, weekends and public holidays.' },
    { q: 'Can a doctor visit my hotel, apartment or Airbnb?', a: 'Yes. A licensed English-speaking doctor can come to your hotel, resort, serviced apartment or Airbnb anywhere in the listed service areas of Da Nang and Hoi An. Just share your address and the name of where you are staying when you contact us.' },
    { q: 'How quickly will a doctor arrive?', a: 'In every listed service area across Da Nang and Hoi An, a licensed doctor arrives at your address within 60 minutes, around the clock. The all-in price is confirmed in writing before the doctor is dispatched.' },
    { q: 'When should I go to hospital instead of requesting a visit?', a: 'For any life-threatening emergency — such as chest pain, difficulty breathing, severe bleeding, loss of consciousness or a serious accident — call 115 for an ambulance or go straight to the nearest hospital emergency department. VietClinics coordinates urgent, non-emergency care only and is not an ambulance or emergency service.' },
    { q: 'Is VietClinics an ambulance or emergency service?', a: 'No. VietClinics is an independent healthcare coordination service. For life-threatening emergencies, dial 115 in Vietnam for an ambulance or go directly to the nearest hospital emergency department.' },
    { q: 'Can I see an English-speaking doctor online instead?', a: 'Yes — a same-day online video consultation with an English-speaking doctor is available and is often the fastest option. If the doctor decides an in-person visit is needed, we arrange a doctor call-out to your address.' },
    { q: 'Do you provide receipts for travel insurance?', a: 'We can help request itemised invoices, receipts and relevant clinic records for your insurer. Coverage and reimbursement depend on your policy and insurer and are not guaranteed. We do not bill insurers directly.' },
    { q: 'What about other cities in Vietnam?', a: 'We coordinate the same 24/7 doctor call-outs in Ho Chi Minh City, Hanoi, Nha Trang, Phu Quoc and Sapa (Sa Pa), with a licensed doctor arriving within 60 minutes in every listed service area.' },
  ];

  const canonicalUrl = 'https://vietclinics.com/urgent-medical-care-hoi-an';

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'MedicalBusiness',
        '@id': `${canonicalUrl}#business`,
        name: 'VietClinics — Urgent Medical Care in Da Nang & Hoi An',
        description:
          'Hotel doctor visits, IV therapy, wound care, online consultations and clinic consultations '
          + 'for tourists and expats across Da Nang & Hoi An, Vietnam.',
        url: canonicalUrl,
        areaServed: [
          { '@type': 'City', name: 'Da Nang', addressCountry: 'VN' },
          { '@type': 'City', name: 'Hoi An', addressCountry: 'VN' },
        ],
        medicalSpecialty: ['GeneralPractice'],
        hasOfferCatalog: {
          '@type': 'OfferCatalog',
          name: 'Urgent Care Services — Da Nang & Hoi An',
          itemListElement: SERVICES.map((s, i) => ({
            '@type': 'Offer',
            position: i + 1,
            name: s.name,
            priceCurrency: 'USD',
            price: s.price.replace(/[^0-9]/g, '') || undefined,
            description: s.desc,
          })),
        },
      },
      {
        '@type': 'WebPage',
        '@id': `${canonicalUrl}#webpage`,
        url: canonicalUrl,
        name: '24/7 Urgent Doctor Call-Outs in Da Nang & Hoi An',
        inLanguage: 'en',
      },
      {
        '@type': 'FAQPage',
        '@id': `${canonicalUrl}#faq`,
        mainEntity: HOI_AN_FAQ.map((f) => ({
          '@type': 'Question',
          name: f.q,
          acceptedAnswer: { '@type': 'Answer', text: f.a },
        })),
      },
      {
        '@type': 'BreadcrumbList',
        '@id': `${canonicalUrl}#breadcrumb`,
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://vietclinics.com' },
          { '@type': 'ListItem', position: 2, name: 'Urgent Medical Care', item: 'https://vietclinics.com/urgent-medical-care' },
          { '@type': 'ListItem', position: 3, name: 'Da Nang & Hoi An', item: canonicalUrl },
        ],
      },
    ],
  };

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>24/7 Doctor Call-Outs in Da Nang & Hoi An | VietClinics</title>
        <meta
          name="description"
          content="Need a doctor in Da Nang or Hoi An? VietClinics coordinates 24/7 hotel, resort, apartment and Airbnb doctor visits, with arrival within 60 minutes in listed areas."
        />
        <link rel="canonical" href="https://vietclinics.com/urgent-medical-care-hoi-an" />
        <meta property="og:title" content="24/7 Doctor Call-Outs in Da Nang & Hoi An | VietClinics" />
        <meta
          property="og:description"
          content="Need a doctor in Da Nang or Hoi An? VietClinics coordinates 24/7 hotel, resort, apartment and Airbnb doctor visits, with arrival within 60 minutes in listed areas."
        />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={HERO_IMAGE} />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <Header />

      {/* ── HERO ─────────────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden bg-white" data-testid="urgent-hoian-hero">
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none"
          style={{ background: 'linear-gradient(180deg, #F0FAF8 0%, #FAFCFB 65%, #FFFFFF 100%)' }}
        />
        <div
          aria-hidden
          className="absolute top-[-10%] right-[-8%] rounded-full pointer-events-none"
          style={{
            width: 620, height: 620,
            background: 'radial-gradient(circle, rgba(31,167,161,0.18) 0%, transparent 70%)',
            filter: 'blur(80px)',
          }}
        />
        <div className="relative z-10 container mx-auto px-4 max-w-7xl pt-12 lg:pt-20 pb-16 lg:pb-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
            <div className="lg:col-span-7 flex flex-col gap-6">
              <div
                className="inline-flex items-center gap-2 self-start px-4 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-widest"
                style={{ background: '#DCEFEC', color: '#1A8F8A' }}
                data-testid="urgent-hoian-eyebrow"
              >
                <MapPin className="h-3.5 w-3.5" />
                Da Nang & Hoi An · Urgent Care Coordination
              </div>

              <h1
                className="font-extrabold tracking-tight text-slate-900 leading-[1.06]"
                style={{ fontSize: 'clamp(2.2rem, 4.4vw, 3.6rem)' }}
                data-testid="urgent-hoian-title"
              >
                24/7 Urgent Doctor Call-Outs in Da Nang & Hoi An
              </h1>

              <p className="text-slate-800 text-lg lg:text-xl leading-relaxed max-w-xl font-medium" data-testid="urgent-hoian-subtitle">
                Fast, reliable, English-speaking medical support for tourists, expats and international visitors across Da Nang & Hoi An.
              </p>

              <p className="text-slate-600 text-base leading-relaxed max-w-xl" data-testid="urgent-hoian-hero-copy">
                Feeling unwell while visiting Da Nang or Hoi An? VietClinics coordinates 24/7 urgent,
                non-emergency doctor call-outs to hotels, resorts, apartments and Airbnbs. In every listed
                service area, a licensed doctor arrives at your address within 60 minutes. Our English-speaking
                coordination team helps arrange the visit and guide you through the next steps.
              </p>

              <div className="flex flex-wrap gap-3 mt-1">
                <a
                  href="#request-care"
                  className="inline-flex items-center gap-2 text-white font-bold rounded-2xl px-7 py-4 transition-all duration-300 hover:-translate-y-0.5"
                  style={{
                    background: 'linear-gradient(135deg, #1FA7A1 0%, #178f8a 100%)',
                    boxShadow: '0 12px 24px rgba(31,167,161,0.25)',
                  }}
                  data-testid="urgent-hoian-cta-request"
                >
                  <Stethoscope className="h-4 w-4" />
                  Get a 24/7 Doctor Call-Out
                </a>
                {whatsappHref && (
                  <a
                    href={whatsappHref}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 bg-white text-[#1FA7A1] font-bold rounded-2xl px-7 py-4 border-2 border-[#1FA7A1] hover:bg-[#1FA7A1]/5 transition-all"
                    data-testid="urgent-hoian-cta-whatsapp"
                  >
                    <MessageCircle className="h-4 w-4" />
                    Chat on WhatsApp
                  </a>
                )}
              </div>

              <p className="flex items-center gap-2 text-xs text-slate-500 mt-1">
                <ShieldCheck className="h-4 w-4 text-[#1FA7A1] flex-shrink-0" />
                Bookings are coordinated by VietClinics. No direct clinic numbers shared.
              </p>

              {/* ── Safety / emergency-redirect strip ──────────────────────── */}
              <div
                className="mt-4 rounded-2xl border border-[#FEC84B]/60 bg-[#FFFAEB] px-4 py-3 flex gap-3 items-start"
                role="note"
                data-testid="urgent-hoian-safety-note"
              >
                <AlertTriangle className="h-5 w-5 text-[#B54708] flex-shrink-0 mt-0.5" />
                <p className="text-[12.5px] text-[#7A2E0E] leading-snug">
                  <strong className="block text-[13px] mb-0.5">For life-threatening emergencies, contact local services immediately.</strong>
                  In Vietnam dial <strong>115</strong> for an ambulance or <strong>113</strong> for police.
                  VietClinics provides urgent, non-emergency doctor call-outs — not emergency response.
                </p>
              </div>
            </div>

            <div className="lg:col-span-5 relative">
              <div
                className="relative rounded-[32px] overflow-hidden aspect-[4/5]"
                style={{ boxShadow: '0 30px 80px rgba(15,42,85,0.18)' }}
              >
                <img
                  src={HERO_IMAGE}
                  alt="English-speaking doctor providing urgent care for a tourist in central Vietnam"
                  className="w-full h-full object-cover"
                  loading="eager"
                  onError={(e) => { e.target.src = HERO_FALLBACK; }}
                />
                <div
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    background:
                      'linear-gradient(135deg, rgba(31,167,161,0.10) 0%, transparent 40%, rgba(31,167,161,0.18) 100%)',
                  }}
                />
              </div>

              <div
                className="hidden sm:block absolute -left-6 bottom-8 bg-white rounded-[24px] p-5 max-w-[260px]"
                style={{ boxShadow: '0 20px 50px rgba(15,42,85,0.16)' }}
              >
                <p className="text-[11px] font-bold text-[#1FA7A1] uppercase tracking-widest mb-2">
                  Response time
                </p>
                <p className="text-2xl font-extrabold text-slate-900 leading-tight">
                  Within 60 min
                </p>
                <p className="text-xs text-slate-500 mt-1 leading-snug">
                  24/7 doctor call-outs — arrival within 60 minutes in listed areas.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── TRUST STRIP ──────────────────────────────────────────────────── */}
      <section className="bg-white border-y border-slate-100" data-testid="urgent-hoian-trust-strip">
        <div className="container mx-auto px-4 max-w-7xl py-10">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-5">
            {TRUST_BADGES.map(({ icon: Icon, label }, i) => (
              <div
                key={label}
                className="flex items-center gap-3"
                data-testid={`urgent-hoian-trust-${i + 1}`}
              >
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ background: '#DCEFEC' }}
                >
                  <Icon className="h-4 w-4 text-[#1A8F8A]" />
                </div>
                <p className="text-xs font-bold text-slate-800 leading-tight">{label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── SERVICES ─────────────────────────────────────────────────────── */}
      <section className="py-20 lg:py-24 bg-white" data-testid="urgent-hoian-services">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="max-w-3xl mb-12">
            <p className="text-[11px] font-bold text-[#1FA7A1] uppercase tracking-widest mb-3">
              Services in Da Nang & Hoi An
            </p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-slate-900 tracking-tight leading-[1.1]">
              Care that comes to you — or sees you at the clinic
            </h2>
            <p className="text-slate-500 mt-4 text-base lg:text-lg leading-relaxed">
              Transparent pricing. English-speaking doctors. Coordinated by VietClinics from request to recovery.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {SERVICES.map((s) => (
              <article
                key={s.id}
                className="bg-white rounded-[24px] border border-slate-100 overflow-hidden flex flex-col hover:-translate-y-1 hover:shadow-xl transition-all duration-300"
                style={{ boxShadow: '0 4px 20px rgba(15,42,85,0.05)' }}
                data-testid={`urgent-hoian-service-${s.id}`}
              >
                <div className="relative aspect-[4/3] overflow-hidden bg-slate-50">
                  <img
                    src={s.image}
                    alt={s.name}
                    loading="lazy"
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                    onError={(e) => { e.target.src = s.fallback; }}
                  />
                  <span className="absolute top-3 left-3 inline-flex items-center bg-white/95 backdrop-blur text-[10px] font-bold text-slate-700 px-2.5 py-1 rounded-full uppercase tracking-wider">
                    {s.eyebrow}
                  </span>
                </div>

                <div className="p-6 flex flex-col flex-1">
                  <h3 className="font-extrabold text-slate-900 text-lg leading-snug mb-1.5">
                    {s.name}
                  </h3>
                  <p
                    className="text-base font-extrabold text-[#1FA7A1] mb-3"
                    data-testid={`urgent-hoian-service-${s.id}-price`}
                  >
                    {s.price}
                  </p>
                  {s.priceNote && (
                    <p className="text-xs text-slate-500 mb-3 leading-snug">{s.priceNote}</p>
                  )}
                  <p className="text-sm text-slate-600 leading-relaxed flex-1">{s.desc}</p>

                  <a
                    href="#request-care"
                    className="mt-5 inline-flex items-center justify-center gap-1.5 w-full text-sm font-bold text-white rounded-full py-2.5 transition-all hover:-translate-y-0.5"
                    style={{ background: 'linear-gradient(135deg, #1FA7A1 0%, #178f8a 100%)' }}
                    data-testid={`urgent-hoian-service-${s.id}-request`}
                  >
                    Request this service
                    <ArrowRight className="h-3.5 w-3.5" />
                  </a>
                </div>
              </article>
            ))}
          </div>

          <p className="text-center text-xs text-slate-500 mt-10 max-w-2xl mx-auto leading-relaxed">
            Prices may vary depending on medical condition and required treatment. Final pricing
            is confirmed by our partner clinic before any service is delivered.
          </p>
        </div>
      </section>

      {/* ── SERVICE AREAS (NEIGHBOURHOODS) ───────────────────────────────── */}
      <section className="py-20 lg:py-24 bg-[#F8FAFC]" data-testid="urgent-hoian-areas">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="max-w-3xl mb-12">
            <p className="text-[11px] font-bold text-[#1FA7A1] uppercase tracking-widest mb-3">
              Service areas
            </p>
            <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight leading-[1.1]">
              Doctor visits across Da Nang & Hoi An
            </h2>
            <p className="text-slate-500 mt-4 text-base lg:text-lg leading-relaxed">
              Wherever you are staying — a beachfront resort, a city-centre hotel, a serviced
              apartment or an Airbnb — a licensed English-speaking doctor can come to your address,
              24/7, with arrival within 60 minutes in listed areas.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <article
              className="bg-white rounded-[24px] border border-slate-100 p-7 sm:p-8"
              style={{ boxShadow: '0 4px 20px rgba(15,42,85,0.05)' }}
              data-testid="urgent-hoian-area-danang"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-11 h-11 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ background: '#DCEFEC' }}>
                  <Building2 className="h-5 w-5 text-[#1A8F8A]" />
                </div>
                <h3 className="text-xl font-extrabold text-slate-900">Doctor visits in Da Nang</h3>
              </div>
              <p className="text-sm text-slate-600 leading-relaxed mb-5">
                From the resorts and apartments along My Khe Beach to the Son Tra peninsula and the
                hotels of central Hai Chau, a <strong>24/7 doctor in Da Nang</strong> can be arranged
                within 60 minutes in listed service areas. A <strong>doctor to hotel in Da Nang</strong>,
                or a doctor to apartment or Airbnb, is equally welcome.
              </p>
              <div className="flex flex-wrap gap-2">
                {DA_NANG_AREAS.map((a) => (
                  <span key={a} className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-700 bg-slate-50 border border-slate-100 rounded-full px-3 py-1.5">
                    <MapPin className="h-3.5 w-3.5 text-[#1FA7A1]" />{a}
                  </span>
                ))}
              </div>
            </article>

            <article
              className="bg-white rounded-[24px] border border-slate-100 p-7 sm:p-8"
              style={{ boxShadow: '0 4px 20px rgba(15,42,85,0.05)' }}
              data-testid="urgent-hoian-area-hoian"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-11 h-11 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ background: '#DCEFEC' }}>
                  <Waves className="h-5 w-5 text-[#1A8F8A]" />
                </div>
                <h3 className="text-xl font-extrabold text-slate-900">Doctor visits in Hoi An</h3>
              </div>
              <p className="text-sm text-slate-600 leading-relaxed mb-5">
                Staying near the lantern-lit Ancient Town or out by An Bang Beach? A{' '}
                <strong>24/7 doctor in Hoi An</strong> — a <strong>doctor to hotel in Hoi An</strong>, or
                a doctor to your homestay, apartment or Airbnb — can be arranged around the clock, with
                arrival within 60 minutes in listed areas.
              </p>
              <div className="flex flex-wrap gap-2">
                {HOI_AN_AREAS.map((a) => (
                  <span key={a} className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-700 bg-slate-50 border border-slate-100 rounded-full px-3 py-1.5">
                    <MapPin className="h-3.5 w-3.5 text-[#1FA7A1]" />{a}
                  </span>
                ))}
              </div>
            </article>
          </div>
        </div>
      </section>

      {/* ── SYMPTOMS & CARE ──────────────────────────────────────────────── */}
      <section className="py-20 lg:py-24 bg-white" data-testid="urgent-hoian-symptoms">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="max-w-3xl mb-12">
            <p className="text-[11px] font-bold text-[#1FA7A1] uppercase tracking-widest mb-3">
              Symptoms &amp; care
            </p>
            <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight leading-[1.1]">
              Common reasons travellers request a doctor
            </h2>
            <p className="text-slate-500 mt-4 text-base lg:text-lg leading-relaxed">
              Urgent medical care for tourists usually starts with a common, non-emergency problem.
              VietClinics coordinates a doctor call-out for issues such as these:
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {SYMPTOMS.map(({ icon: Icon, label }, i) => (
              <div
                key={label}
                className="flex items-center gap-3 bg-[#F8FAFC] rounded-2xl border border-slate-100 px-4 py-4"
                data-testid={`urgent-hoian-symptom-${i + 1}`}
              >
                <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: '#DCEFEC' }}>
                  <Icon className="h-4 w-4 text-[#1A8F8A]" />
                </div>
                <span className="text-sm font-semibold text-slate-800 leading-snug">{label}</span>
              </div>
            ))}
          </div>

          <div className="mt-8 rounded-[24px] border border-slate-100 bg-[#F0FAF8] p-7 sm:p-8" data-testid="urgent-hoian-care-note">
            <div className="flex items-start gap-4">
              <div className="w-11 h-11 rounded-2xl flex items-center justify-center flex-shrink-0 bg-white">
                <Stethoscope className="h-5 w-5 text-[#1A8F8A]" />
              </div>
              <div>
                <h3 className="text-lg font-extrabold text-slate-900 mb-2">What the doctor may recommend</h3>
                <p className="text-sm text-slate-600 leading-relaxed">
                  After assessing the patient at your address, the doctor may recommend suitable care —
                  such as <strong>IV therapy</strong> for dehydration, wound cleaning or{' '}
                  <strong>wound dressing</strong>, a prescription, a visit to a clinic, or a referral —
                  when clinically appropriate and available. The doctor explains the options and next
                  steps before any care is provided.
                </p>
              </div>
            </div>
          </div>

          <div className="mt-6 rounded-2xl border border-[#FEC84B]/60 bg-[#FFFAEB] px-5 py-4 flex gap-3 items-start" role="note" data-testid="urgent-hoian-symptoms-emergency">
            <AlertTriangle className="h-5 w-5 text-[#B54708] flex-shrink-0 mt-0.5" />
            <p className="text-[13px] text-[#7A2E0E] leading-snug">
              <strong>For life-threatening emergencies, call 115 or go to the nearest hospital emergency department.</strong>{' '}
              VietClinics coordinates urgent, non-emergency doctor call-outs — not emergency response.
            </p>
          </div>
        </div>
      </section>

      {/* ── CONDITIONS & SERVICES (additive SEO) ─────────────────────────── */}
      <UrgentConditionsServices testId="urgent-hoian-conditions" />

      {/* ── HOW IT WORKS ─────────────────────────────────────────────────── */}
      <section className="py-20 bg-[#F8FAFC]" data-testid="urgent-hoian-how-it-works">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="max-w-3xl mb-12 text-center mx-auto">
            <p className="text-[11px] font-bold text-[#1FA7A1] uppercase tracking-widest mb-3">
              How it works
            </p>
            <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight leading-[1.1]">
              From request to recovery — in 4 simple steps
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {STEPS.map((step) => (
              <div
                key={step.n}
                className="bg-white rounded-[20px] border border-slate-100 p-6"
                style={{ boxShadow: '0 4px 20px rgba(15,42,85,0.04)' }}
                data-testid={`urgent-hoian-step-${step.n}`}
              >
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center font-extrabold text-white mb-4"
                  style={{ background: 'linear-gradient(135deg, #1FA7A1 0%, #178f8a 100%)' }}
                >
                  {step.n}
                </div>
                <h3 className="font-extrabold text-slate-900 text-base leading-snug mb-2">
                  {step.title}
                </h3>
                <p className="text-sm text-slate-600 leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── REQUEST FORM + WHATSAPP SUPPORT CARD ─────────────────────────── */}
      <section id="request-care" className="py-20 bg-white" data-testid="urgent-hoian-request-section">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
            {/* Form */}
            <div
              className="lg:col-span-2 bg-white rounded-[28px] border border-slate-100 p-6 sm:p-10"
              style={{ boxShadow: '0 20px 60px rgba(15,42,85,0.08)' }}
            >
              <div className="mb-6">
                <p className="text-[11px] font-bold text-[#1FA7A1] uppercase tracking-widest mb-2">
                  Care Request
                </p>
                <h3 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
                  Request urgent care in Da Nang & Hoi An
                </h3>
                <p className="text-slate-500 mt-2 text-sm sm:text-base leading-relaxed">
                  Share a few details — a licensed English-speaking doctor arrives at your hotel,
                  apartment or Airbnb within 60 minutes, 24/7.
                </p>
              </div>

              {status.sent ? (
                <div className="text-center max-w-md mx-auto py-8" data-testid="urgent-hoian-form-success">
                  <div className="w-14 h-14 mx-auto rounded-2xl bg-emerald-50 flex items-center justify-center mb-4">
                    <CheckCircle2 className="h-7 w-7 text-emerald-500" />
                  </div>
                  <h4 className="text-2xl font-extrabold text-slate-900 tracking-tight">
                    Request received
                  </h4>
                  <p className="text-slate-500 mt-2 leading-relaxed">
                    Thanks {form.fullName.split(' ')[0]}. Our team will contact you on WhatsApp
                    very shortly with confirmation and next steps.
                  </p>
                  {whatsappHref && (
                    <a
                      href={whatsappHref}
                      target="_blank"
                      rel="noreferrer"
                      className="mt-6 inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1ebe57] text-white font-bold rounded-full px-6 py-3 transition-colors"
                      data-testid="urgent-hoian-form-whatsapp-link"
                    >
                      <MessageCircle className="h-4 w-4" />
                      Continue on WhatsApp
                    </a>
                  )}
                </div>
              ) : (
                <form
                  onSubmit={handleSubmit}
                  className="grid grid-cols-1 sm:grid-cols-2 gap-4"
                  data-testid="urgent-hoian-form"
                >
                  <Field label="Full Name *">
                    <input
                      type="text"
                      required
                      value={form.fullName}
                      onChange={onChange('fullName')}
                      placeholder="Jane Smith"
                      className={inputCls}
                      data-testid="urgent-hoian-input-name"
                    />
                  </Field>
                  <Field label="WhatsApp / Phone *">
                    <input
                      type="tel"
                      required
                      value={form.whatsapp}
                      onChange={onChange('whatsapp')}
                      placeholder="+1 ..."
                      className={inputCls}
                      data-testid="urgent-hoian-input-whatsapp"
                    />
                  </Field>

                  <Field label="Email">
                    <input
                      type="email"
                      value={form.email}
                      onChange={onChange('email')}
                      placeholder="you@example.com"
                      className={inputCls}
                      data-testid="urgent-hoian-input-email"
                    />
                  </Field>
                  <Field label="Country">
                    <select
                      value={form.country}
                      onChange={onChange('country')}
                      className={inputCls}
                      data-testid="urgent-hoian-input-country"
                    >
                      <option value="">Select your country</option>
                      {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </Field>

                  <Field label="Preferred Service *">
                    <select
                      required
                      value={form.service}
                      onChange={onChange('service')}
                      className={inputCls}
                      data-testid="urgent-hoian-input-service"
                    >
                      <option value="">Select a service</option>
                      {SERVICE_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                      <option value="Not sure / Other">Not sure / Other</option>
                    </select>
                  </Field>

                  <Field label="Preferred Date / Time">
                    <input
                      type="datetime-local"
                      value={form.date}
                      onChange={onChange('date')}
                      className={inputCls}
                      data-testid="urgent-hoian-input-date"
                    />
                  </Field>

                  <Field label="Symptoms / Message" className="sm:col-span-2">
                    <textarea
                      rows={4}
                      value={form.symptoms}
                      onChange={onChange('symptoms')}
                      placeholder="Briefly describe symptoms, current location (hotel name), and any allergies or existing conditions."
                      className={`${inputCls} resize-none h-auto py-2.5`}
                      data-testid="urgent-hoian-input-symptoms"
                    />
                  </Field>

                  {status.error && (
                    <p className="sm:col-span-2 text-sm text-red-500 font-medium">{status.error}</p>
                  )}

                  <div className="sm:col-span-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mt-2">
                    <p className="flex items-center gap-2 text-xs text-slate-500">
                      <ShieldCheck className="h-4 w-4 text-[#1FA7A1] flex-shrink-0" />
                      Coordinated by VietClinics — your details stay private.
                    </p>
                    <button
                      type="submit"
                      disabled={status.loading}
                      className="inline-flex items-center justify-center gap-2 bg-[#1FA7A1] hover:bg-[#178f8a] disabled:opacity-60 text-white font-bold rounded-full px-7 py-3.5 transition-all hover:-translate-y-0.5"
                      data-testid="urgent-hoian-form-submit"
                    >
                      {status.loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                      Submit Request
                    </button>
                  </div>
                </form>
              )}
            </div>

            {/* WhatsApp side card */}
            <aside className="lg:col-span-1" data-testid="urgent-hoian-whatsapp-card">
              <div
                className="rounded-[28px] p-8 text-white relative overflow-hidden"
                style={{ background: 'linear-gradient(135deg, #0f2b55 0%, #1E5BA8 55%, #1FA7A1 100%)' }}
              >
                <div
                  aria-hidden
                  className="absolute inset-0 opacity-[0.10] pointer-events-none"
                  style={{ backgroundImage: 'radial-gradient(circle at 80% 20%, white 0%, transparent 50%)' }}
                />
                <div className="relative">
                  <div className="w-12 h-12 rounded-2xl bg-white/15 flex items-center justify-center mb-4 backdrop-blur-sm">
                    <MessageCircle className="h-5 w-5 text-white" />
                  </div>
                  <h3 className="text-2xl font-extrabold tracking-tight leading-tight mb-2">
                    Need Help Now?
                  </h3>
                  <p className="text-white/85 text-sm leading-relaxed mb-6">
                    Chat with our team on WhatsApp for immediate assistance — a doctor can be at
                    your door within 60 minutes, 24/7.
                  </p>
                  {whatsappHref ? (
                    <a
                      href={whatsappHref}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-2 bg-white text-[#1FA7A1] font-bold rounded-full px-6 py-3 hover:-translate-y-0.5 transition-all"
                      data-testid="urgent-hoian-whatsapp-card-cta"
                    >
                      <MessageCircle className="h-4 w-4" />
                      Chat on WhatsApp
                    </a>
                  ) : (
                    <a
                      href="#request-care"
                      className="inline-flex items-center gap-2 bg-white text-[#1FA7A1] font-bold rounded-full px-6 py-3 hover:-translate-y-0.5 transition-all"
                      data-testid="urgent-hoian-whatsapp-card-cta-fallback"
                    >
                      <Stethoscope className="h-4 w-4" />
                      Use the request form
                    </a>
                  )}
                </div>
              </div>

              <div className="bg-white rounded-[24px] border border-slate-100 p-6 mt-5"
                style={{ boxShadow: '0 4px 20px rgba(15,42,85,0.05)' }}
              >
                <p className="text-[11px] font-bold text-[#1FA7A1] uppercase tracking-widest mb-2">
                  Why VietClinics
                </p>
                <ul className="space-y-2.5 mt-3">
                  {[
                    'Verified Da Nang & Hoi An partner clinics',
                    'English-speaking doctors',
                    'Transparent USD pricing',
                    'Hotel & in-clinic options',
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-2 text-sm text-slate-700">
                      <CheckCircle2 className="h-4 w-4 text-[#1FA7A1] flex-shrink-0 mt-0.5" />
                      <span className="leading-snug">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* ── FAQ ──────────────────────────────────────────────────────────── */}
      <section className="py-20 lg:py-24 bg-[#F8FAFC]" data-testid="urgent-hoian-faq">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="max-w-3xl mb-10">
            <p className="text-[11px] font-bold text-[#1FA7A1] uppercase tracking-widest mb-3">
              FAQs
            </p>
            <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight leading-[1.1]">
              Da Nang & Hoi An doctor call-outs — your questions
            </h2>
          </div>

          <div className="space-y-3">
            {HOI_AN_FAQ.map((f, i) => (
              <details
                key={f.q}
                className="group bg-white rounded-2xl border border-slate-100 px-5 sm:px-6 py-4"
                style={{ boxShadow: '0 4px 20px rgba(15,42,85,0.04)' }}
                data-testid={`urgent-hoian-faq-${i + 1}`}
              >
                <summary className="flex items-center justify-between gap-4 cursor-pointer list-none font-bold text-slate-900 text-base leading-snug">
                  <span className="flex items-start gap-3">
                    <HelpCircle className="h-5 w-5 text-[#1FA7A1] flex-shrink-0 mt-0.5" />
                    {f.q}
                  </span>
                  <ChevronDown className="h-5 w-5 text-slate-400 flex-shrink-0 transition-transform duration-300 group-open:rotate-180" />
                </summary>
                <p className="text-sm text-slate-600 leading-relaxed mt-3 pl-8">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default UrgentMedicalCareHoiAnPage;
